

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateLeaveType, fetchLeaveTypes } from "../../redux/actions/leaveTypeActions";
import { useNavigate, useParams } from "react-router-dom";
import { FiCheck, FiArrowLeft, FiType, FiFileText, FiCalendar } from "react-icons/fi";

const EditLeaveType = () => {
  const { id } = useParams(); // leave type ID from URL
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { leaveTypes, updating, updateError } = useSelector((state) => state.leaveType);

  const leaveType = leaveTypes.find((lt) => lt.id === parseInt(id)) || {};

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    total_leaves: 0,
  });

  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Prefill form when leaveType is available
  useEffect(() => {
    if (leaveTypes.length === 0) {
      dispatch(fetchLeaveTypes());
    } else if (leaveType?.id) {
      setFormData({
        name: leaveType.name || "",
        description: leaveType.description || "",
        total_leaves: leaveType.total_leaves || 0,
      });
    }
  }, [leaveTypes, leaveType, dispatch]);

  // Redirect after successful update
  useEffect(() => {
    if (updateSuccess) {
      navigate("/dashboard/view-leave-types");
    }
  }, [updateSuccess, navigate]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "total_leaves" ? parseInt(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.total_leaves) return;

    try {
      await dispatch(updateLeaveType(id, formData));
      setUpdateSuccess(true); // trigger redirect
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  const isFormValid = formData.name.trim() !== "" && formData.total_leaves > 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard/view-leave-types")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="text-lg" />
            Back to Leave Types
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiCheck className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">Edit Leave Type</h1>
                <p className="text-cyan-100">Update leave type details</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {updateError && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                <p className="text-red-800 dark:text-red-300 font-medium">
                  {typeof updateError === "string" ? updateError : "Something went wrong"}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiType className="text-sm" />
                      Leave Type Name <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Enter leave type name"
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 transition placeholder-gray-500 dark:placeholder-gray-400"
                    required
                    disabled={updating}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiCalendar className="text-sm" />
                      Total Leaves <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={formData.total_leaves}
                    onChange={(e) => handleChange("total_leaves", e.target.value)}
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 transition placeholder-gray-500 dark:placeholder-gray-400"
                    required
                    disabled={updating}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiFileText className="text-sm" />
                      Description
                    </div>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder="Enter description (optional)"
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 transition placeholder-gray-500 dark:placeholder-gray-400 h-24 resize-none"
                    disabled={updating}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/view-leave-types")}
                  className="flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl transition-colors"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={updating || !isFormValid}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updating ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <FiCheck className="text-lg" />
                      Update Leave Type
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditLeaveType;
